// Lib
import React, { useCallback, useRef } from 'react';

const MODAL_INTERACTIVE_CONTENT_SELECTOR =
    '.ImageModalV2Content-frame, .ImageModalV2-edit-overlay .image-modal-content, .ImageModalV2-drawing-overlay, .ImageModalV2PrimaryTools, .ImageModalV2SecondaryTools, .ImageModalToolbarSecondaryTools, .ImageEditorModalToolbar, .drawing-editor-toolbar-container, .FloatingPanel, .RomaPopover';

/**
 * Determines whether a modal click happened on image content or a toolbar control.
 */
export const isModalInteractiveContentTarget = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest(MODAL_INTERACTIVE_CONTENT_SELECTOR);

type UseImageModalV2BackgroundCloseProps = {
    close: (event?: React.MouseEvent<HTMLDivElement> | globalThis.MouseEvent) => void;
};

/**
 * Matches V1's mousedown/click pairing so drags that start inside modal content
 * don't close on release. Capture phase records the mousedown target before pan
 * handlers call stopPropagation. The outer modal close path saves drawing through its registered close callback.
 */
export const useImageModalV2BackgroundClose = ({ close }: UseImageModalV2BackgroundCloseProps) => {
    const wasMouseDownOnClosingTargetRef = useRef(false);

    const handleModalMouseDown = useCallback((event: React.MouseEvent) => {
        wasMouseDownOnClosingTargetRef.current = !isModalInteractiveContentTarget(event.target);
    }, []);

    const handleModalClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!wasMouseDownOnClosingTargetRef.current) return;
            wasMouseDownOnClosingTargetRef.current = false;

            close(event);
        },
        [close],
    );

    return { handleModalMouseDown, handleModalClick };
};
